import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react'
import { VerifyEmailProps } from '../interfaces/formDataInterface'
import { toast } from 'react-toastify'
import useDismissAlert from '../Hooks/useDismissAlert'
import useResendOTP from '../Hooks/useResendOTP'
import axiosReq from '../interceptor'
import { MessageAlert } from '../components/message-alert'

const VerifyEmail: React.FC<VerifyEmailProps> = ({
  isLoading,
  setIsLoading,
  areFieldsSet,
  isVerified,
  onVerify,
  onNext,
  onFieldsSet
}) => {
  const [otp, setOtp] = useState<string>('')
  const email = localStorage.getItem('email') ?? ''
  const { isResending, resendTimer, resendOTP, resendError } =
    useResendOTP(email)
  const [verifyError, setVerifyError] = useDismissAlert({ message: '' }, 5000)

  useEffect(() => {
    onFieldsSet(otp.length === 6)
  }, [otp, onFieldsSet])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOtp(e.target.value)
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setVerifyError({ message: '' })
    setIsLoading(true)

    try {
      const response = await axiosReq.post('/app/v2/auth/sign-up-verify-otp', {
        otp,
        email
      })

      if (response.status === 200) {
        toast.success('Email verified successfully!')
        onVerify(true)
        onNext()
      } else {
        setVerifyError({
          message:
            response.data?.message || 'Verification failed. Please try again.'
        })
        onVerify(false)
      }
    } catch (error: any) {
      setVerifyError({
        message:
          error.response?.data?.message ||
          'An error occurred. Please try again.'
      })
      onVerify(false)
    } finally {
      setIsLoading(false)
    }
  }

  if (isVerified) {
    return (
      <section>
        <div className="alert alert-info" role="alert">
          <small>Email already verified. Continue to the next step.</small>
        </div>
        <button
          className="btn btn-primary rounded-pill w-100 mt-2"
          onClick={onNext}
        >
          Continue
        </button>
      </section>
    )
  }

  return (
    <section>
      <form onSubmit={handleSubmit}>
        <div className="form-outline mb-3">
          <label className="form-label text-white" htmlFor="otp">
            Enter OTP <span className="text-danger">*</span>
          </label>
          <input
            type="password"
            id="otp"
            name="otp"
            className="form-control"
            placeholder="eg. 123456"
            maxLength={6}
            required
            value={otp}
            onChange={handleChange}
            disabled={isLoading}
          />
        </div>

        {verifyError.message && (
          <MessageAlert message={verifyError.message} type="error" />
        )}

        {resendError?.message && (
          <MessageAlert message={resendError.message} type="error" />
        )}

        <button
          className="btn btn-primary rounded-pill w-100 btn-block mb-3"
          type="submit"
          disabled={isLoading || !areFieldsSet}
        >
          {isLoading ? 'Verifying...' : 'Verify'}
        </button>
        <div>
          <p className="mb-0 text-white-50" style={{ fontSize: '12px' }}>
            Didn&apos;t receive email?{' '}
            <button
              className="text-white bg-transparent border-0"
              onClick={(e) => {
                e.preventDefault()
                resendOTP()
              }}
              disabled={isResending || resendTimer > 0}
            >
              {isResending
                ? 'Resending...'
                : resendTimer > 0
                  ? `Resend Code in (${resendTimer}s)`
                  : 'Resend Code'}
            </button>
          </p>
        </div>
      </form>
    </section>
  )
}

export default VerifyEmail
